/**
 * The server-wide chat channels (ADR-068's amendment "Global, Trade and Help"): Global, Trade and Help, which
 * every character is in until it leaves one; each channel's pace; the repeat guard's key and window; how many
 * channel lines a listener is handed a second; how many joins, leaves and mutes an account makes a minute; and
 * how long a GM may mute. The gateway holds channel lines to these numbers, the page reads the words; each
 * number is a knob the amendment's open question 1 asks the author to confirm.
 */

/**
 * The channels, in the order the page lists them. On the wire and in the store a channel is its word.
 */
export const CHAT_CHANNELS = ["global", "trade", "help"] as const;

/**
 * One channel.
 */
export type ChatChannel = (typeof CHAT_CHANNELS)[number];

/**
 * The scope a mute names to cover every channel.
 */
export const MUTE_ALL = "all";

/**
 * What a mute covers: one channel, or all of them.
 */
export type MuteScope = ChatChannel | typeof MUTE_ALL;

/**
 * The longest channel word the wire carries, so a channel a later server opens is a value and not a malformed
 * frame.
 */
export const MAX_CHANNEL_WORD_CHARS = 16;

/**
 * A channel's own limit: at most `lines` lines in a window of `windowMs`, per connection and per account.
 */
export interface ChannelPace {
  readonly lines: number;
  readonly windowMs: number;
}

/**
 * Each channel's pace: Global and Help two lines in 10 seconds, Trade one in 30 seconds, since Bible 9.1 rate
 * limits Trade.
 */
export const CHANNEL_PACE: Readonly<Record<ChatChannel, ChannelPace>> = Object.freeze({
  global: Object.freeze({ lines: 2, windowMs: 10_000 }),
  trade: Object.freeze({ lines: 1, windowMs: 30_000 }),
  help: Object.freeze({ lines: 2, windowMs: 10_000 }),
});

/**
 * How long the same text from one account is refused in every channel after it was said, in milliseconds.
 */
export const CHANNEL_REPEAT_MS = 60_000;

/**
 * The most texts the repeat guard holds for one account, the oldest dropped first.
 */
export const CHANNEL_REPEAT_HELD = 32;

/**
 * How many channel lines one listener is handed a second beside its own; past it a line is not delivered to that
 * listener and is counted.
 */
export const CHANNEL_LINES_PER_SECOND = 10;

/**
 * How many joins, leaves, mutes and lifts one account makes in a minute, since each writes a row.
 */
export const CHANNEL_CHANGES_PER_MINUTE = 10;

/**
 * The longest mute, in minutes: 30 days.
 */
export const MAX_MUTE_MINUTES = 30 * 24 * 60;

/**
 * The longest mute a GM holding only the short mute may give, in minutes.
 */
export const SHORT_MUTE_MAX_MINUTES = 60;

const CHANNEL_SET: ReadonlySet<string> = new Set(CHAT_CHANNELS);

/**
 * White space, which the repeat key reads as one space per run.
 */
const SPACE_RUN = /\s+/g;

/**
 * What the repeat key drops: every default-ignorable code point (soft hyphens, zero-width and invisible
 * characters, variation selectors, tags, fillers) and every combining mark, so a line made different only by
 * something a reader cannot see, or by marks laid over its letters, is the same line.
 */
const HIDDEN = /[\p{Default_Ignorable_Code_Point}\p{M}]/gu;

/**
 * Whether a word is one of the channels, as sent: lower case, whole.
 *
 * @param word The word.
 * @returns True for `global`, `trade` or `help`.
 */
export function isChatChannel(word: string): word is ChatChannel {
  return CHANNEL_SET.has(word);
}

/**
 * Whether a word is a mute's scope: a channel, or `all`.
 *
 * @param word The word.
 * @returns True for a channel or `all`.
 */
export function isMuteScope(word: string): word is MuteScope {
  return word === MUTE_ALL || CHANNEL_SET.has(word);
}

/**
 * Whether a mute of a scope covers a channel.
 *
 * @param scope The mute's scope.
 * @param channel The channel.
 * @returns True when the scope is `all` or the channel itself.
 */
export function muteCovers(scope: MuteScope, channel: ChatChannel): boolean {
  return scope === MUTE_ALL || scope === channel;
}

/**
 * The key the repeat guard compares lines by: compatibility-decomposed, with every default-ignorable code point and
 * every combining mark taken out, in lower case, with every run of white space read as one space, trimmed. A line
 * said again in another case, with other spacing, in full-width letters, with invisible characters inside it or with
 * marks added or taken away is the same line, so "caf\u00e9" and "cafe" are one line. Look-alike letters of other
 * scripts are not folded (ADR-068's amendment "Global, Trade and Help", item 9).
 *
 * @param text The line, as the text rule admitted it.
 * @returns The key.
 * @example
 * repeatKey("  WTS   Iron\u200b Swo\u0301rd ");
 * // "wts iron sword"
 */
export function repeatKey(text: string): string {
  return text.normalize("NFKD").replace(HIDDEN, "").toLowerCase().replace(SPACE_RUN, " ").trim();
}
